(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => reduced.matches ? 'instant' : 'smooth';

  class YuumaeProductGallery extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.rail = this.querySelector('[data-rail]');
      this.slides = [...this.rail.children];
      this.index = 0;
      this.zoomButton = this.querySelector('[data-gallery-zoom]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.zoomButton?.addEventListener('click', () => {
        const source = this.slides[this.index]?.querySelector('img');
        if (!source || !this.dialog) return;
        const enlarged = source.cloneNode();
        enlarged.removeAttribute('id');
        enlarged.sizes = '94vw';
        enlarged.loading = 'eager';
        this.dialog.querySelector('[data-gallery-enlarged]').replaceChildren(enlarged);
        this.dialog.showModal();
      }, options);
      this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog.close(), options);
      this.dialog?.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-thumbnail]');
        if (!thumb) return;
        event.preventDefault(); this.select(thumb.dataset.thumbnail);
      }, options);
      this.rail.addEventListener('keydown', event => {
        if (event.target !== this.rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
        this.goTo(Math.max(0, Math.min(this.slides.length - 1, next)));
      }, options);
      this.rail.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const index = Math.round(this.rail.scrollLeft / this.rail.clientWidth);
          if (index === this.index) return;
          this.pause(this.slides[this.index]); this.index = index;
          this.update();
        });
      }, { ...options, passive: true });
      this.update();
      if (this.querySelector('model-viewer')) window.Shopify?.loadFeatures?.([{name:'model-viewer-ui',version:'1.0',onLoad: () => {
        if (!this.isConnected || !window.Shopify.ModelViewerUI) return;
        this.models = [...this.querySelectorAll('model-viewer')].map(model => new window.Shopify.ModelViewerUI(model));
      }}]);
    }
    update() {
      const current = this.slides[this.index];
      if (!current) return;
      if (this.zoomButton) this.zoomButton.hidden = current.dataset.mediaType !== 'image';
      this.querySelectorAll('[data-thumbnail]').forEach(thumb => {
        if (thumb.dataset.thumbnail === current.dataset.mediaId) thumb.setAttribute('aria-current','true');
        else thumb.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-gallery-index]');
      if (count) count.textContent = this.index + 1;
      // Offscreen interactive media must not capture keyboard focus.
      this.slides.forEach((slide, index) => { slide.inert = index !== this.index; });
    }
    pause(slide) {
      slide?.querySelectorAll('video').forEach(video => video.pause());
      slide?.querySelectorAll('iframe').forEach(frame => {
        const url = new URL(frame.src);
        if (url.hostname.includes('youtube')) frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}), url.origin);
        if (url.hostname.includes('vimeo')) frame.contentWindow?.postMessage(JSON.stringify({method:'pause'}), url.origin);
      });
      this.models?.forEach(model => model.pause());
    }
    goTo(index) { this.rail.scrollTo({left:index * this.rail.clientWidth,behavior:behavior()}); }
    select(id) { const index = this.slides.findIndex(slide => slide.dataset.mediaId === String(id)); if (index >= 0) this.goTo(index); }
    disconnectedCallback() { this.dialog?.close(); this.abort?.abort(); cancelAnimationFrame(this.frame); this.models?.forEach(model => model.destroy?.()); }
  }

  class YuumaeProduct extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.sticky = this.querySelector('[data-sticky-add]');
      this.mobile = window.matchMedia('(max-width:767px)');
      this.selector = this.querySelector('[data-variant-selector]');
      this.selector?.addEventListener('change', () => this.changeVariant(), options);
      this.addEventListener('change', event => {
        if (event.target.name === 'selling_plan' || event.target.closest('.subscriptions_app_embed_block')) this.syncPrice();
        if (event.target.name === 'quantity') this.syncQuantity();
      }, options);
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-quantity-change]');
        if (!control) return;
        const input = this.querySelector('[name=quantity]');
        if (control.dataset.quantityChange === '1') input.stepUp(); else input.stepDown();
        input.dispatchEvent(new Event('change',{bubbles:true}));
      }, options);
      this.addEventListener('yuumae:product-submitting', event => {
        if (this.selector) this.selector.disabled = event.detail.busy;
        const widget = this.querySelector('.subscriptions_app_embed_block');
        if (widget) widget.inert = event.detail.busy;
      }, options);
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (this.selector && id && [...this.selector.options].some(option => option.value === id)) {
          this.selector.value = id; this.changeVariant(false);
        }
      }, options);
      this.bindState();
      if (this.sticky) {
        const scheduleSticky = () => {
          cancelAnimationFrame(this.stickyFrame);
          this.stickyFrame = requestAnimationFrame(() => this.updateSticky());
        };
        window.addEventListener('scroll', scheduleSticky, {...options, passive:true});
        window.addEventListener('resize', scheduleSticky, options);
        this.resize = new ResizeObserver(entries => {
          if (!this.sticky.hidden) document.documentElement.style.setProperty('--yuumae-sticky-height',`${entries[0].target.getBoundingClientRect().height}px`);
        }); this.resize.observe(this.sticky);
      }
    }
    bindState() {
      this.querySelectorAll('[data-quantity-change]').forEach(button => {button.hidden = false;});
      this.subscriptionObserver?.disconnect();
      this.subscriptionObserver = new MutationObserver(() => this.activateSubscriptionWidget());
      this.subscriptionObserver.observe(this.querySelector('.yuumae-pdp-form'), {childList:true});
      this.activateSubscriptionWidget();
      this.syncQuantity(); this.syncPrice();
      this.primary = this.querySelector('[data-primary-add]');
      this.updateSticky();
    }
    activateSubscriptionWidget() {
      const state = this.querySelector('[data-purchase-state]');
      const widget = state.querySelector('.subscriptions_app_embed_block');
      const input = state.querySelector('.yuumae-pdp-form .selected-selling-plan-id');
      if (!widget || !input) return;
      const fallback = state.querySelector('.yuumae-purchase-options');
      const available = !!fallback && !!widget.querySelector(`.shopify_subscriptions_app_block[data-variant-id="${state.dataset.variantId}"]`);
      const initialPlan = fallback?.querySelector('[name="selling_plan"]:checked')?.value || '';
      const activating = available && widget.hidden;
      widget.hidden = !available;
      input.disabled = !available;
      if (fallback) { fallback.hidden = available; fallback.disabled = available; }
      if (available) this.prepareSubscriptionDropdowns();
      if (activating) this.restoreSubscriptionChoice(initialPlan);
      this.syncPrice();
    }
    prepareSubscriptionDropdowns() {
      const state = this.querySelector('[data-purchase-state]');
      const choices = [...state.querySelectorAll('.yuumae-purchase-options [name="selling_plan"]')];
      const form = state.querySelector('.yuumae-pdp-form');
      state.querySelectorAll('[data-radio-type="one_time_purchase"]').forEach(radio => {
        const label = radio.closest('.group_list_item')?.querySelector('span');
        if (label) label.textContent = state.dataset.oneTimeLabel;
        radio.setAttribute('aria-label',`${state.dataset.oneTimeLabel}, ${radio.dataset.variantPrice}`);
      });
      state.querySelectorAll('.group_dropdown').forEach((dropdown, index) => {
        const group = dropdown.closest('.subscription_group');
        const radio = group.querySelector('[data-radio-type="selling_plan_group"]');
        radio?.removeAttribute('tabindex');
        const heading = group.querySelector('.group_name');
        if (heading && !heading.querySelector('.yuumae-subscription-badge')) {
          const badge = document.createElement('span');
          badge.className = 'yuumae-subscription-badge';
          badge.hidden = true;
          heading.replaceChildren(document.createTextNode(`${state.dataset.subscriptionLabel} `), badge);
        }
        dropdown.id ||= `${form.id}-frequency-${index}`;
        let label = group.querySelector('.yuumae-frequency-label');
        if (!label) {
          label = document.createElement('label');
          label.className = 'yuumae-frequency-label';
          dropdown.before(label);
        }
        label.htmlFor = dropdown.id;
        label.textContent = state.dataset.frequencyLabel;
        dropdown.removeAttribute('aria-label');
        const prices = new Set([...dropdown.options].map(option => option.dataset.variantPrice));
        [...dropdown.options].forEach(option => {
          const choice = choices.find(input => input.value === option.value);
          if (choice?.dataset.frequency) option.textContent = prices.size > 1 ? `${choice.dataset.frequency} — ${choice.dataset.price}` : choice.dataset.frequency;
        });
      });
    }
    restoreSubscriptionChoice(planId) {
      const state = this.querySelector('[data-purchase-state]');
      const widget = state.querySelector(`.subscriptions_app_embed_block .shopify_subscriptions_app_block[data-variant-id="${state.dataset.variantId}"]`);
      if (!widget) return;
      const dropdown = [...widget.querySelectorAll('select')].find(select => [...select.options].some(option => option.value === planId));
      if (planId && dropdown) {
        dropdown.value = planId;
        dropdown.dispatchEvent(new Event('change', {bubbles:true}));
        return;
      }
      const radio = [...widget.querySelectorAll('input[data-radio-type]')].find(input => planId ? input.dataset.sellingPlanId === planId : input.dataset.radioType === 'one_time_purchase');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', {bubbles:true}));
      }
    }
    syncQuantity() {
      const input = this.querySelector('[name=quantity]');
      if (!input) return;
      const value = Number(input.value);
      this.querySelector('[data-quantity-change="-1"]').disabled = value <= Number(input.min);
      this.querySelector('[data-quantity-change="1"]').disabled = input.max !== '' && value >= Number(input.max);
    }
    syncPrice() {
      const state = this.querySelector('[data-purchase-state]');
      const appInput = state.querySelector('.yuumae-pdp-form .selected-selling-plan-id:not(:disabled)');
      const options = [...state.querySelectorAll('.yuumae-purchase-options [name="selling_plan"]')];
      const radio = appInput ? options.find(option => option.value === appInput.value) : options.find(option => option.checked);
      if (appInput && radio) radio.checked = true;
      if (radio) {
        state.querySelectorAll('[data-purchase-price]').forEach(node => {node.textContent = radio.dataset.price;});
        const unit = state.querySelector('.yuumae-pdp-unit');
        if (unit) unit.textContent = radio.dataset.unitPrice || '';
        const compare = state.querySelector('[data-compare-price]');
        compare.textContent = radio.dataset.compare; compare.hidden = !radio.dataset.compare;
        const button = state.querySelector('[data-primary-add]');
        if (state.dataset.available === 'true' && !button.hasAttribute('aria-busy')) {
          button.querySelector('[data-button-label]').textContent = radio.value ? state.dataset.addSubscription : state.dataset.addOnce;
        }
      }
      state.querySelectorAll('.shopify_subscriptions_app_policy').forEach(policy => { policy.hidden = !radio?.value; });
      state.querySelectorAll('.group_dropdown').forEach(dropdown => {
        const group = dropdown.closest('.subscription_group');
        const groupRadio = group.querySelector('[data-radio-type="selling_plan_group"]');
        const choice = options.find(option => option.value === dropdown.value);
        const badge = group.querySelector('.yuumae-subscription-badge');
        if (badge && choice) {
          const saving = Number(choice.dataset.savingPercent);
          badge.textContent = saving > 0 ? `−${saving}\u00a0%` : '';
          badge.hidden = !(saving > 0);
        }
        if (groupRadio && choice) groupRadio.setAttribute('aria-label',`${group.querySelector('.group_name').textContent.trim()}, ${choice.dataset.price}, ${choice.dataset.frequency}`);
      });
      if (this.sticky) {
        this.sticky.querySelector('[data-sticky-price]').textContent = state.querySelector('[data-purchase-price]').textContent;
        this.sticky.querySelector('[data-sticky-plan]').textContent = radio?.value ? `${state.dataset.subscriptionLabel} · ${radio.dataset.frequency}` : '';
        this.sticky.querySelector('button').disabled = state.dataset.available !== 'true';
        this.sticky.querySelector('[data-button-label]').textContent = state.querySelector('[data-button-label]').textContent;
      }
    }
    updateSticky() {
      // Anchor jumps can skip both sides of an IntersectionObserver threshold.
      if (this.sticky && this.primary) this.sticky.hidden = !(this.mobile.matches || this.hasAttribute('data-sticky-desktop')) || this.primary.getBoundingClientRect().bottom >= 0;
    }
    async changeVariant(updateUrl = true) {
      this.request?.abort();
      const request = new AbortController(); this.request = request;
      const previous = this.querySelector('[data-purchase-state]');
      const currentId = previous.dataset.variantId;
      const quantity = previous.querySelector('[name=quantity]').value;
      const sellingPlan = previous.querySelector('.yuumae-pdp-form .selected-selling-plan-id:not(:disabled)')?.value ?? previous.querySelector('[name=selling_plan]:checked')?.value;
      const variantId = this.selector.value;
      const url = new URL(this.dataset.productUrl,location.origin);
      if (this.dataset.templateView) url.searchParams.set('view',this.dataset.templateView);
      url.searchParams.set('variant',variantId); url.searchParams.set('section_id',this.dataset.sectionId);
      if (sellingPlan) url.searchParams.set('selling_plan',sellingPlan);
      this.setAttribute('aria-busy','true');
      this.querySelectorAll('[data-add-button]').forEach(button => {button.disabled = true;});
      const error = this.querySelector('[data-variant-error]'); error.hidden = true;
      const timeout = setTimeout(() => request.abort(),15000);
      try {
        const response = await fetch(url,{signal:request.signal,headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error();
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        if (this.request !== request || !this.isConnected) return;
        const next = doc.querySelector('[data-purchase-state]');
        if (!next || next.dataset.variantId !== variantId) throw new Error();
        const input = next.querySelector('[name=quantity]');
        input.value = quantity;
        if (!input.checkValidity()) input.value = input.min;
        const appWidget = previous.querySelector('.subscriptions_app_embed_block');
        const oldForm = previous.querySelector('.yuumae-pdp-form');
        const appInput = oldForm.querySelector('.selected-selling-plan-id');
        const nextOptions = [...next.querySelectorAll('.yuumae-purchase-options [name=selling_plan]')];
        const preservedChoice = nextOptions.find(option => option.value === sellingPlan);
        if (preservedChoice) preservedChoice.checked = true;
        const nextPlan = next.querySelector('[name=selling_plan]:checked')?.value || '';
        if (appInput && appWidget) {
          const nextForm = next.querySelector('.yuumae-pdp-form');
          oldForm.replaceChildren(...nextForm.childNodes);
          oldForm.append(appInput);
          nextForm.replaceWith(oldForm);
          next.querySelector('.subscriptions_app_embed_block').replaceWith(appWidget);
        }
        previous.replaceWith(next);
        if (updateUrl) { const current = new URL(location.href);current.searchParams.set('variant',variantId);current.searchParams.delete('selling_plan');history.replaceState({},'',current); }
        if (appInput) {
          oldForm.dispatchEvent(new Event('change', {bubbles:true}));
          this.restoreSubscriptionChoice(nextPlan);
        }
        this.bindState();
        if (next.dataset.mediaId) this.querySelector('yuumae-product-gallery')?.select(next.dataset.mediaId);
      } catch (failure) {
        // A newer request owns the selector. Never restore stale state over it.
        if (this.request !== request || !this.isConnected) return;
        this.selector.value = currentId; error.textContent = this.dataset.error; error.hidden = false;
        this.querySelectorAll('[data-add-button]').forEach(button => {button.disabled = previous.dataset.available !== 'true';});
      } finally { clearTimeout(timeout); if (this.request === request) this.removeAttribute('aria-busy'); }
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); cancelAnimationFrame(this.stickyFrame); this.resize?.disconnect(); this.subscriptionObserver?.disconnect(); }
  }
  class YuumaeProductNavigation extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.build();
      document.addEventListener('shopify:section:load', () => this.build(), {signal:this.abort.signal});
      document.addEventListener('shopify:section:unload', () => requestAnimationFrame(() => this.build()), {signal:this.abort.signal});
    }
    build() {
      const topics = [...document.querySelectorAll('main [data-product-topic][id]')];
      this.querySelector('nav').replaceChildren(...topics.map(section => {
        const link = document.createElement('a');
        link.href = `#${section.id}`; link.textContent = section.dataset.productTopic;
        return link;
      }));
      this.hidden = topics.length < 2;
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  if (!customElements.get('yuumae-product-gallery')) customElements.define('yuumae-product-gallery',YuumaeProductGallery);
  if (!customElements.get('yuumae-product')) customElements.define('yuumae-product',YuumaeProduct);
  if (!customElements.get('yuumae-product-navigation')) customElements.define('yuumae-product-navigation',YuumaeProductNavigation);
})();
